/**
 * Pure functions over the QXO capital structure. No React, no fetch — unit-testable.
 * Inputs default to the filed figures in qxoFilingsData.ts; pass overrides to stress them.
 */

import {
  DEBT_STACK,
  PREFERRED_STACK,
  PRO_FORMA_FY25,
  SHARE_COUNT,
  WARRANTS,
  WORKING_CAPITAL,
  type DebtTranche,
  type PreferredLayer,
} from '@/components/qxo/qxoFilingsData';

export interface DilutionStep {
  id: string;
  label: string;
  /** Incremental common shares, millions (net of treasury-method proceeds for warrants). */
  sharesM: number;
  /** Whether the instrument converts / is exercised at this price. */
  active: boolean;
  detail: string;
}

export interface DilutionLadder {
  price: number;
  basicM: number;
  steps: DilutionStep[];
  fullyDilutedM: number;
  /** Preferred that stays as a senior claim at this price, $M. */
  unconvertedPreferredM: number;
}

/** Treasury-stock method: net new shares from a warrant tranche at `price`. */
export function warrantNetShares(sharesM: number, strike: number, price: number): number {
  if (price <= strike) return 0;
  return sharesM * (1 - strike / price);
}

/** Series B mandatory convertible: shares delivered per the filed collar, millions. */
export function seriesBShares(price: number, liquidationM = 575, lowRef = 16.5, highRef = 20.2126): number {
  const maxShares = liquidationM / lowRef; // 34.8M
  const minShares = liquidationM / highRef; // 28.4M
  if (price <= lowRef) return maxShares;
  if (price >= highRef) return minShares;
  return liquidationM / price;
}

export function buildDilutionLadder(
  price: number,
  opts: {
    basicM?: number;
    preFundedM?: number;
    preferred?: PreferredLayer[];
    warrants?: readonly { tranche: string; sharesM: number; strike: number }[];
  } = {},
): DilutionLadder {
  const basicM = opts.basicM ?? SHARE_COUNT.commonOutstandingM;
  const preFundedM = opts.preFundedM ?? SHARE_COUNT.preFundedWarrantsM;
  const preferred = opts.preferred ?? PREFERRED_STACK;
  const warrants = opts.warrants ?? WARRANTS;

  const steps: DilutionStep[] = [
    {
      id: 'prefunded',
      label: 'Pre-funded warrants ($0.00001 strike)',
      sharesM: preFundedM,
      active: true,
      detail: 'Counted in basic EPS by the company; economically common.',
    },
  ];

  let unconvertedPreferredM = 0;
  for (const layer of preferred) {
    if (layer.name.includes('Series B')) {
      const shares = seriesBShares(price, layer.liquidationM);
      steps.push({
        id: 'seriesB',
        label: layer.name,
        sharesM: shares,
        active: true,
        detail: `Mandatory in May 2028; ${shares.toFixed(1)}M shares at $${price.toFixed(2)}.`,
      });
      continue;
    }
    const inMoney = layer.conversionPrice != null && price > layer.conversionPrice;
    if (inMoney) {
      steps.push({
        id: layer.name,
        label: layer.name,
        sharesM: layer.conversionSharesM,
        active: true,
        detail: `Converts at $${layer.conversionPrice?.toFixed(3)}; in the money.`,
      });
    } else {
      unconvertedPreferredM += layer.liquidationM;
      steps.push({
        id: layer.name,
        label: layer.name,
        sharesM: 0,
        active: false,
        detail: `Out of the money below $${layer.conversionPrice?.toFixed(2)}; stays a $${(layer.liquidationM / 1000).toFixed(1)}B claim ahead of common.`,
      });
    }
  }

  for (const w of warrants) {
    const net = warrantNetShares(w.sharesM, w.strike, price);
    steps.push({
      id: w.tranche,
      label: `${w.tranche} (${w.sharesM}M @ $${w.strike})`,
      sharesM: net,
      active: net > 0,
      detail: net > 0 ? `Treasury method: ${net.toFixed(1)}M net shares.` : 'Out of the money.',
    });
  }

  const fullyDilutedM = basicM + steps.reduce((sum, s) => sum + s.sharesM, 0);
  return { price, basicM, steps, fullyDilutedM, unconvertedPreferredM };
}

export interface FixedCharges {
  grossDebtM: number;
  annualInterestM: number;
  weightedRatePct: number;
  floatingShareOfDebtPct: number;
  preferredLiquidationM: number;
  annualPreferredDividendsM: number;
  totalFixedChargesM: number;
}

export function computeFixedCharges(debt: DebtTranche[] = DEBT_STACK, preferred: PreferredLayer[] = PREFERRED_STACK): FixedCharges {
  const grossDebtM = debt.reduce((s, d) => s + d.principalM, 0);
  const annualInterestM = debt.reduce((s, d) => s + (d.principalM * d.ratePct) / 100, 0);
  const floatingM = debt.filter((d) => d.rateType === 'floating').reduce((s, d) => s + d.principalM, 0);
  const preferredLiquidationM = preferred.reduce((s, p) => s + p.liquidationM, 0);
  const annualPreferredDividendsM = preferred.reduce((s, p) => s + p.annualDividendM, 0);
  return {
    grossDebtM,
    annualInterestM,
    weightedRatePct: grossDebtM ? (annualInterestM / grossDebtM) * 100 : 0,
    floatingShareOfDebtPct: grossDebtM ? (floatingM / grossDebtM) * 100 : 0,
    preferredLiquidationM,
    annualPreferredDividendsM,
    totalFixedChargesM: annualInterestM + annualPreferredDividendsM,
  };
}

export interface EnterpriseValueView {
  price: number;
  equityM: number;
  grossDebtM: number;
  cashM: number;
  netDebtM: number;
  preferredM: number;
  /** EV counting unconverted preferred as a debt-like claim. */
  evWithPreferredM: number;
  evExPreferredM: number;
  combinedEbitdaM: number;
  evToEbitdaWithPreferred: number;
  evToEbitdaExPreferred: number;
  netDebtToEbitda: number;
  netDebtPlusPreferredToEbitda: number;
}

/**
 * Market-implied multiple on the FY25 combined adjusted EBITDA of the three platforms.
 * Cash defaults to the Jun 30 26 balance (pre-TopBuild cash outlay); pass `cashM` to override.
 */
export function computeEnterpriseValue(
  price: number,
  opts: { sharesM?: number; cashM?: number; ebitdaM?: number; debt?: DebtTranche[]; preferred?: PreferredLayer[] } = {},
): EnterpriseValueView {
  const debt = opts.debt ?? DEBT_STACK;
  const preferred = opts.preferred ?? PREFERRED_STACK;
  const ladder = buildDilutionLadder(price, { preferred });
  const sharesM = opts.sharesM ?? ladder.fullyDilutedM;
  const equityM = price * sharesM;
  const grossDebtM = debt.reduce((s, d) => s + d.principalM, 0);
  const cashM = opts.cashM ?? WORKING_CAPITAL[WORKING_CAPITAL.length - 1].cash;
  const netDebtM = grossDebtM - cashM;
  const preferredM = ladder.unconvertedPreferredM;
  const combinedEbitdaM = opts.ebitdaM ?? PRO_FORMA_FY25.combinedAdjEbitda;
  const evWithPreferredM = equityM + netDebtM + preferredM;
  const evExPreferredM = equityM + netDebtM;
  return {
    price,
    equityM,
    grossDebtM,
    cashM,
    netDebtM,
    preferredM,
    evWithPreferredM,
    evExPreferredM,
    combinedEbitdaM,
    evToEbitdaWithPreferred: evWithPreferredM / combinedEbitdaM,
    evToEbitdaExPreferred: evExPreferredM / combinedEbitdaM,
    netDebtToEbitda: netDebtM / combinedEbitdaM,
    netDebtPlusPreferredToEbitda: (netDebtM + preferredM) / combinedEbitdaM,
  };
}

/** EBITDA left after cash interest and preferred dividends — before capex, taxes and one-offs. */
export function cashAfterFixedCharges(ebitdaM: number, charges: FixedCharges = computeFixedCharges()): {
  ebitdaM: number;
  interestM: number;
  preferredM: number;
  residualM: number;
  coverage: number;
} {
  return {
    ebitdaM,
    interestM: charges.annualInterestM,
    preferredM: charges.annualPreferredDividendsM,
    residualM: ebitdaM - charges.totalFixedChargesM,
    coverage: charges.totalFixedChargesM ? ebitdaM / charges.totalFixedChargesM : 0,
  };
}

/** Margin the Beacon perimeter needs to hit a 2030 EBITDA target at a given revenue. */
export function impliedMarginPct(targetEbitdaM: number, revenueM: number): number {
  return revenueM ? (targetEbitdaM / revenueM) * 100 : 0;
}
